import { Button, FileText, Folder, HoverTooltip, Lock, NavIconButton } from "@repo/ui";
import { cn } from "@repo/utils";
import type { ExplorerItem } from "../types";

type Props = {
  item: ExplorerItem;
  level: number;
  isSelected: boolean;
  isExpanded: boolean;
  hasChildren: boolean;
  setIsExpanded: () => void;
  handleClick: (e: React.MouseEvent | React.KeyboardEvent) => void;
  handleDoubleClick?: () => void;
  isDisabled?: boolean;
  windowId?: string;
  activeItemId?: number | null;
};

export const ItemEntry = ({
  item,
  level,
  isSelected,
  hasChildren,
  isExpanded,
  setIsExpanded,
  handleClick,
  handleDoubleClick,
  isDisabled = false,
  windowId,
  activeItemId,
}: Props) => {
  const isItem = "taskId" in item && "statusCode" in item;
  const isLocked = isItem && !!item.lock;
  const lockedByName = isLocked ? item.lock?.displayName : undefined;

  return (
    <div
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      aria-disabled={isDisabled}
      data-item-id={item.id}
      data-window-id={windowId}
      onClick={(e) => handleClick(e)}
      onDoubleClick={handleDoubleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick(e);
        }
      }}
      className={cn(
        "group flex min-w-0 items-center gap-2 rounded border border-transparent px-2 text-left text-sm select-none focus:ring-2 focus:ring-blue-500 focus:outline-none",
        {
          "cursor-pointer hover:bg-gray-100": !isDisabled,
          "cursor-not-allowed opacity-60": isDisabled,
          "ml-0": level > 0,
          "border-blue-200 bg-blue-50": isSelected && isItem && !isLocked,
          "border-blue-300 bg-blue-100": isSelected && isLocked,
          "shadow-[0_0_0_1px_rgba(59,130,246,0.5)]": isSelected && isLocked,
          "pt-1 pl-0": hasChildren,
          "font-bold": activeItemId === item.id,
        }
      )}
      style={{
        marginLeft: `${level > 0 ? level * 16 : 0}px`,
        width: `calc(100% - ${level > 0 ? level * 16 : 0}px)`,
      }}
    >
      <Button
        icon={
          !isItem ? (
            <Folder
              className={cn("size-3 shrink-0", {
                "fill-blue-100 text-blue-500": hasChildren,
                "text-gray-500": !hasChildren,
              })}
            />
          ) : (
            <FileText className="size-3 shrink-0 text-gray-600" />
          )
        }
        variant="outline"
        align="center"
        justify="center"
        size="icon_md"
        className="relative z-10 size-6 shrink-0 rounded-full"
        title={hasChildren ? "Status Group" : "Task Item"}
      />
      <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
        {hasChildren && (
          <span className="shrink-0 text-[12px] font-semibold text-gray-700">[{item.id}] -</span>
        )}
        <span
          className={cn("min-w-0 flex-1 truncate text-[12px] text-gray-700", {
            "font-semibold": hasChildren,
          })}
          title={item.name}
        >
          {item.name}
        </span>
      </div>
      {isLocked && (
        <HoverTooltip content={lockedByName ?? "Locked"}>
          <Lock
            className={cn("relative z-10 h-3.5 w-3.5 shrink-0", {
              "text-amber-500": isDisabled,
              "text-blue-500": !isDisabled,
            })}
          />
        </HoverTooltip>
      )}
      {hasChildren && (
        <div className="relative z-10 shrink-0">
          <NavIconButton
            text=""
            isOpen={isExpanded}
            showChevron
            customButtonClassName="h-fit w-fit"
            toggleOpen={setIsExpanded}
          />
        </div>
      )}
    </div>
  );
};
